<template>
    <app-layout>
        <template #header>
            <div class="pr-8 sm:flex items-center justify-between">
                <div class="flex">
                    <svg
                        width="30"
                        height="30"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke="url(#paint0_linear_68_19)"
                            stroke-width="1.8"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
                        />
                        <defs>
                            <linearGradient
                                id="paint0_linear_68_19"
                                x1="4.56256"
                                y1="4.33394"
                                x2="24.9101"
                                y2="26.2819"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop stop-color="#FF8D47" />
                                <stop offset="0.147634" stop-color="#FF7447" />
                                <stop offset="0.454086" stop-color="#FF4247" />
                                <stop offset="0.636926" stop-color="#E92A5B" />
                                <stop offset="0.740367" stop-color="#DD1C67" />
                                <stop offset="0.842991" stop-color="#D31071" />
                                <stop offset="1" stop-color="#CF0283" />
                            </linearGradient>
                        </defs>
                    </svg>

                    <div class="ml-4">
                        <h2
                            class="text-xl font-semibold leading-6 tracking-tight sm:text-2xl text-[#18181B]"
                        >
                            {{ $t("onboarding.title") }}
                        </h2>
                        <p
                            class="inter text-sm font-medium mt-[2px] text-[#70707B]"
                        >
                            {{ $t("onboarding.description") }}
                        </p>
                    </div>
                </div>
            </div>
        </template>

        <div class="px-20 py-12">
            <nav aria-label="Progress">
                <ol role="list" class="overflow-hidden">
                    <li
                        v-for="(step, stepIdx) in steps"
                        :key="step.name"
                        :class="[
                            stepIdx !== steps.length - 1 ? 'pb-10' : '',
                            'relative',
                        ]"
                    >
                        <template v-if="step.status === 'complete'">
                            <div
                                v-if="stepIdx !== steps.length - 1"
                                class="left-4 top-4 mt-0.5 w-0.5 bg-z-orange-800 absolute -ml-px h-full"
                                aria-hidden="true"
                            />
                            <Link
                                :href="step.href"
                                class="group relative flex items-start"
                            >
                                <span class="h-9 flex items-center">
                                    <span
                                        class="h-8 w-8 bg-z-orange-800 group-hover:bg-z-orange-800 relative z-10 flex items-center justify-center rounded-full"
                                    >
                                        <CheckIcon
                                            class="h-5 w-5 text-white"
                                            aria-hidden="true"
                                        />
                                    </span>
                                </span>
                                <span class="ml-4 min-w-0 flex flex-col">
                                    <span class="text-sm font-medium">{{
                                        step.name
                                    }}</span>
                                    <span
                                        class="max-w-xl text-sm text-iron-500"
                                        >{{ step.description }}</span
                                    >
                                </span>
                            </Link>
                        </template>
                        <template v-else-if="step.status === 'current'">
                            <div
                                v-if="stepIdx !== steps.length - 1"
                                class="left-4 top-4 mt-0.5 w-0.5 bg-iron-300 absolute -ml-px h-full"
                                aria-hidden="true"
                            />
                            <Link
                                :href="step.href"
                                class="group relative flex items-start"
                                aria-current="step"
                            >
                                <span
                                    class="h-9 flex items-center"
                                    aria-hidden="true"
                                >
                                    <span
                                        class="h-8 w-8 border-z-orange-500 bg-white relative z-10 flex items-center justify-center rounded-full border-2"
                                    >
                                        <span
                                            class="h-2.5 w-2.5 text-z-orange-600 rounded-full"
                                        />
                                    </span>
                                </span>
                                <span class="ml-4 min-w-0 flex flex-col">
                                    <span
                                        class="text-sm font-medium text-z-orange-600"
                                        >{{ step.name }}</span
                                    >
                                    <span
                                        class="max-w-xl text-sm text-iron-500 hover:text-iron-700"
                                        >{{ step.description }}</span
                                    >
                                </span>
                            </Link>
                        </template>
                        <template v-else>
                            <div
                                v-if="stepIdx !== steps.length - 1"
                                class="left-4 top-4 mt-0.5 w-0.5 bg-iron-300 absolute -ml-px h-full"
                                aria-hidden="true"
                            />
                            <div class="group relative flex items-start">
                                <span
                                    class="h-9 flex items-center"
                                    aria-hidden="true"
                                >
                                    <span
                                        class="h-8 w-8 border-iron-300 bg-white relative z-10 flex items-center justify-center rounded-full border-2"
                                    >
                                    </span>
                                </span>
                                <span class="ml-4 min-w-0 flex flex-col">
                                    <span
                                        class="text-sm font-medium text-iron-500"
                                        >{{ step.name }}</span
                                    >
                                    <span
                                        class="max-w-xl text-sm text-iron-500"
                                        >{{ step.description }}</span
                                    >
                                </span>
                            </div>
                        </template>
                    </li>
                </ol>
            </nav>
        </div>
    </app-layout>
</template>

<script lang="ts" setup>
import AppLayout from "../../../../Layouts/AppLayout.vue";
import { CheckIcon } from "@heroicons/vue/20/solid";

import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";

import route from "ziggy-js";

type Step = {
    name: string;
    description: string;
    href: string;
    status: "complete" | "current" | "upcoming";
};

type SharedData = {
    onboarding: {
        availability?: boolean;
        event_type?: boolean;
        customize_event_type?: boolean;
        first_booking?: boolean;
    };
};

type EventType = {
    uuid: string;
    id: number;
};

const props = defineProps<{
    firstEventType: EventType | undefined;
}>();

const page = usePage<SharedData>();

const { t } = useI18n();

const onboarding = computed(() => {
    return page.props.onboarding;
});

const steps: Step[] = [
    {
        name: t("onboarding.availability.title"),
        description: t("onboarding.availability.description"),
        href: route("app.availabilities.index"),
        status: onboarding.value.availability ? "complete" : "current",
    },
    {
        name: t("onboarding.event_types.title"),
        description: t("onboarding.event_types.description"),
        href: route("app.event-types.index"),
        status: onboarding.value.event_type
            ? "complete"
            : onboarding.value.availability
              ? "current"
              : "upcoming",
    },
    {
        name: t("onboarding.event_type_customization.title"),
        description: t("onboarding.event_type_customization.description"),
        href: props.firstEventType
            ? route("app.event-types.show", {
                  eventType: props.firstEventType.id,
              })
            : "#",
        status: onboarding.value.customize_event_type
            ? "complete"
            : onboarding.value.event_type
              ? "current"
              : "upcoming",
    },
    {
        name: t("onboarding.try-it.title"),
        description: t("onboarding.try-it.description"),
        href: props.firstEventType
            ? route("public.event-types.show", {
                  eventTypeUuid: props.firstEventType.uuid,
              })
            : "#",
        status: onboarding.value.first_booking
            ? "complete"
            : onboarding.value.customize_event_type
              ? "current"
              : "upcoming",
    },
];
</script>
